import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@postdaily/ui/components/card";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { NotificationList } from "@/components/notifications/notification-list";
import { NotificationPreferencesForm } from "@/components/notifications/notification-preferences-form";
import { PageHeader } from "@/components/page-header";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { useTRPC } from "@/utils/trpc";

export const Route = createFileRoute("/(app)/settings/notifications")({
  component: RouteComponent,
});

function RouteComponent() {
  const { currentWorkspace } = useCurrentWorkspace();

  if (!currentWorkspace) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          size="lg"
          title="Notifications"
          description="Preferences and recent workspace notifications."
        />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  return <NotificationsPanel workspaceId={currentWorkspace.id} />;
}

function NotificationsPanel({ workspaceId }: { workspaceId: string }) {
  const trpc = useTRPC();
  const preferences = useQuery(
    trpc.notifications.getPreferences.queryOptions({ workspaceId }),
  );

  if (preferences.isPending) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          size="lg"
          title="Notifications"
          description="Preferences and recent workspace notifications."
        />
        <Skeleton className="h-72 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        size="lg"
        title="Notifications"
        description="Preferences and recent workspace notifications."
      />
      {preferences.isError ? (
        <Card>
          <CardHeader>
            <CardTitle>Preferences unavailable</CardTitle>
            <CardDescription>{preferences.error.message}</CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <NotificationPreferencesForm
          key={workspaceId}
          preferences={preferences.data}
          workspaceId={workspaceId}
        />
      )}
      <Card>
        <CardHeader>
          <CardTitle>Recent Notifications</CardTitle>
          <CardDescription>
            Product notifications created for this workspace.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <NotificationList workspaceId={workspaceId} />
        </CardContent>
      </Card>
    </div>
  );
}
